import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useCurrentOrg } from '@/app/entities/org/model'
import { payWithCloudPayments, useCloudPaymentsWidget } from '@/app/entities/cloudPayments'
import { openPaymentResultModal } from '@/app/entities/paymentResult'
import { useMe } from '@/common/entities/me/model/me'
import { trackPackSuccessByPackageId } from '@/common/lib/yandexMetrica/paymentGoals'

export type PackageOption = {
    id: number
    tokens: number
    price: number
    name: string
}

export type AddTokensFormValues = {
    packageId: number
}

type UseAddTokensFormOptions = {
    onSuccess?: () => void
}

const CLOUDPAYMENTS_PUBLIC_ID = __CLOUDPAYMENTS_PUBLIC_ID__

const PACKAGES: Record<number, Omit<PackageOption, 'id'>> = {
    1: { tokens: 500, price: 2000, name: 'Хватает на 20 генераций' },
    2: { tokens: 1500, price: 5400, name: 'Хватает на 60 генераций' },
    3: { tokens: 15000, price: 48000, name: 'Хватает на 600 генераций' },
}

export const useAddTokensForm = ({ onSuccess }: UseAddTokensFormOptions = {}) => {
    const { data: currentOrg } = useCurrentOrg()
    const { me } = useMe()
    const [isPaying, setIsPaying] = useState(false)
    const [paymentError, setPaymentError] = useState<string | null>(null)

    const { isWidgetReady, widgetError, getWidgetInstance } = useCloudPaymentsWidget({
        onClose: () => setIsPaying(false),
    })

    const packageOptions = useMemo<PackageOption[]>(
        () =>
            Object.entries(PACKAGES).map(([id, pkg]) => ({
                id: Number(id),
                tokens: pkg.tokens,
                price: pkg.price,
                name: pkg.name,
            })),
        []
    )

    const form = useForm<AddTokensFormValues>({
        defaultValues: {
            packageId: packageOptions[0]?.id,
        },
        mode: 'onSubmit',
    })

    const selectedPackageId = form.watch('packageId')

    const handlePackageSelect = (packageId: number) => {
        form.setValue('packageId', packageId, { shouldValidate: true, shouldDirty: true })
        form.clearErrors('packageId')
    }

    const handlePay = (values: AddTokensFormValues) => {
        if (isPaying) return
        const normalizedPackageId = Number(values.packageId)
        const selectedPackage = packageOptions.find(pkg => pkg.id === normalizedPackageId) ?? null
        if (!selectedPackage) {
            form.setError('packageId', { message: 'Пожалуйста, выберите пакет.' })
            return
        }
        if (!currentOrg?.id || !me?.id) {
            setPaymentError('Не удалось определить организацию или пользователя.')
            return
        }
        if (!isWidgetReady) {
            setPaymentError('Платежный виджет еще не загружен.')
            return
        }

        const widget = getWidgetInstance()
        if (!widget) {
            setPaymentError('Платежный виджет не инициализировался.')
            return
        }

        const invoicePostfix = Math.floor(Date.now() / 1000)
        const invoiceId = `TOKENS_${currentOrg.id}_${selectedPackage.id}_${invoicePostfix}`
        const purchaseLabel = selectedPackage.name
        const amount = selectedPackage.price
        const tokens = selectedPackage.tokens

        setPaymentError(null)
        setIsPaying(true)

        payWithCloudPayments({
            widget,
            options: {
                publicId: CLOUDPAYMENTS_PUBLIC_ID,
                description: `Докупка ${selectedPackage.tokens} токенов (${selectedPackage.name})`,
                amount: selectedPackage.price,
                currency: 'RUB',
                invoiceId,
                accountId: me.id,
                ...(me.email ? { email: me.email } : {}),
                data: {
                    organization_id: currentOrg.id,
                    user_id: me.id,
                    type: 'additional_tokens',
                    package_id: selectedPackage.id,
                    tokens: selectedPackage.tokens,
                },
            },
            onSuccess: () => {
                setIsPaying(false)
                trackPackSuccessByPackageId(selectedPackage.id)
                openPaymentResultModal({
                    status: 'success',
                    purchaseType: 'tokens',
                    purchaseLabel,
                    amount,
                    tokens,
                    onSettledSuccess: () => {
                        if (onSuccess) {
                            onSuccess()
                            return
                        }
                        window.location.href = `/organizations/${currentOrg.id}`
                    },
                })
            },
            onFail: result => {
                setIsPaying(false)
                openPaymentResultModal({
                    status: 'fail',
                    purchaseType: 'tokens',
                    purchaseLabel,
                    amount,
                    tokens,
                    failReason: result.reason,
                })
            },
        })
    }

    const handleSubmit = form.handleSubmit(handlePay)

    useEffect(() => {
        if (packageOptions.length === 0) return
        if (form.getValues('packageId')) return
        form.setValue('packageId', packageOptions[0].id)
    }, [form, packageOptions])

    useEffect(() => {
        if (!selectedPackageId) return
        form.clearErrors('packageId')
    }, [form, selectedPackageId])

    return {
        form,
        packageOptions,
        widgetError: paymentError ?? widgetError,
        isPaying,
        isWidgetReady,
        selectedPackageId,
        handlePackageSelect,
        handleSubmit,
    }
}
